import join from 'url-join';
import { formValueSelector } from 'redux-form';

import { asyncAction, http, createAction } from '../util/index';

import { showToast } from '../components/toast/actions';
import { TranslatedCopy } from '../intl/TranslatedCopy';
import { getUserData } from '../user/selectors';
import { getGamedayAccount } from '../gamedayAccount/selectors';
import { DEFAULT_ADDRESS_STRUCTURE } from '../carts/util/structures';
import { mergedAddresses } from './selectors';
import { assignCartProductGroups } from '../carts/actionCreators';
import { ADDRESS_TYPES } from './constants';
import { unionBy } from '../util/lodash';

const api = join(import.meta.env.VITE_API_URL, 'v1');
const api2 = join(import.meta.env.VITE_API2_URL, 'v1');
const loqateFind = join(import.meta.env.VITE_LOQATE_FIND_URL, `?Key=${import.meta.env.VITE_LOQATE_KEY}`);
const loqateRetrieve = join(import.meta.env.VITE_LOQATE_RETRIEVE_URL, `?Key=${import.meta.env.VITE_LOQATE_KEY}`);

export const toggleSidebar = createAction('TOGGLE_SIDEBAR');
export const closeSidebar = createAction('CLOSE_SIDEBAR');
export const setEditAddress = createAction('SET_EDIT_ADDRESS');
export const setMergedAddresses = createAction('SET_MERGED_ADDRESSES');
export const clearMergedAddresses = createAction('CLEAR_MERGED_ADDRESS');
export const removeAddressById = createAction('REMOVE_MERGED_ADDRESS');

const normalizeCustomAddress = (address, accountNumber, isLocked = false) => ({
  ...DEFAULT_ADDRESS_STRUCTURE,
  shipToId: accountNumber,
  ...address,
  isLocked,
  isActive: true,
  isCustom: true,
  type: ADDRESS_TYPES.GAMEDAY
});

export const requestAddresses = asyncAction({
  baseName: 'REQUEST_ADDRESSES',
  action: action => () => (dispatch, getState) => {
    dispatch(action.start());

    return http
      .get(join(api2, '/users/current/addresses'))
      .then(({ data: { objects } }) => {
        const account = getGamedayAccount(getState());
        const normalizedAddresses = objects.map(address => normalizeCustomAddress(address, account.accountNumber));

        dispatch(setMergedAddresses(normalizedAddresses));

        return dispatch(action.success(objects));
      })
      .catch(error => {
        dispatch(
          showToast({
            type: 'error',
            message: new TranslatedCopy({ id: 'global.toast.systemError' }).toString(),
            error
          })
        );
        dispatch(action.error(error));
      });
  }
});

export const requestAddressById = asyncAction({
  baseName: 'REQUEST_ADDRESS_BY_ID',
  action: action => ({ addressId, isLocked = true }) => (dispatch, getState) => {
    return http
      .get(join(api2, `/addresses/${addressId}`))
      .then(({ data }) => {
        const state = getState();
        const account = getGamedayAccount(state);
        const normalizedResponse = normalizeCustomAddress(data, account.accountNumber, isLocked);

        dispatch(setMergedAddresses(normalizedResponse));

        return dispatch(action.success(data));
      })
      .catch(error => dispatch(action.error(error)));
  }
});

export const requestSapAddresses = asyncAction({
  baseName: 'REQUEST_SAP_ADDRESSES',
  action: action => ({ accountNumber }) => dispatch => {
    dispatch(action.start(accountNumber));
    return http
      .get(join(api, `/account/${accountNumber}/addresses`))
      .then(({ data: response }) => dispatch(action.success(response.shipToAddresses)))
      .catch(error => {
        dispatch(
          showToast({
            type: 'error',
            message: new TranslatedCopy({ id: 'global.toast.systemError' }).toString(),
            error
          })
        );
        dispatch(action.error({ error }));
      });
  }
});

function generateSAPAddressId() {
  return Math.random().toString(36).substring(2) + new Date().getTime().toString(36);
}

export const requestAllAddresses = ({ keepExistingMerged = false } = {}) => (dispatch, getState) => {
  const state = getState();
  const account = getGamedayAccount(state);
  const {
    accountNumber,
    billable: { accountNumber: billableAccountNumber }
  } = account;
  const customAddresses = [];
  const sapAddresses = [];

  const handleSapAddressResponse = (response, isComp = false, isBillable = false) => {
    response.forEach(addressAccount => {
      addressAccount.addresses.forEach(address => {
        sapAddresses.push({
          ...DEFAULT_ADDRESS_STRUCTURE,
          id: generateSAPAddressId(),
          shipToId: addressAccount.accountNumber.replace(/^0+/, ''),
          originalShipToId: addressAccount.accountNumber,
          ...address,
          cityStateZip: `${address.city}, ${address.state} ${address.zipCode}`,
          isComp,
          isBillable,
          isLocked: true,
          isActive: true,
          isCustom: false,
          account: addressAccount.accountNumber,
          type: ADDRESS_TYPES.SAP
        });
      });
    });
  };

  const requests = [
    dispatch(requestAddresses()).then(({ payload: response }) => {
      response.forEach(address =>
        customAddresses.push({
          ...DEFAULT_ADDRESS_STRUCTURE,
          shipToId: accountNumber,
          ...address,
          cityStateZip: `${address.city}, ${address.state} ${address.zipCode}`,
          isLocked: false,
          isActive: true,
          isCustom: true,
          type: ADDRESS_TYPES.GAMEDAY
        })
      );
    }),
    dispatch(requestSapAddresses({ accountNumber })).then(({ payload: response }) =>
      handleSapAddressResponse(response, true, false)
    )
  ];

  if (billableAccountNumber)
    requests.push(
      dispatch(requestSapAddresses({ accountNumber: billableAccountNumber })).then(({ payload: response }) =>
        handleSapAddressResponse(response, false, true)
      )
    );

  return Promise.all(requests).then(() => {
    // Merge existing custom addresses with the updates from this one.
    // Now that we merge by id, request made when account is updated will always merged with prevous accounts
    let existing;

    if (keepExistingMerged) {
      existing = mergedAddresses(state).filter(address => address.isCustom);
    } else {
      dispatch(clearMergedAddresses());
      existing = [];
    }

    dispatch(setMergedAddresses([...sapAddresses, ...unionBy('id', customAddresses, existing)]));
  });
};

// formerly saveAddress
export const createAddress = asyncAction({
  baseName: 'CREATE_ADDRESS',
  action: action => values => (dispatch, getState) => {
    dispatch(action.start(values));
    const displayNotification = values.displayNotification !== false;
    const rawAddress = values.address;

    const state = getState();
    const { id: userId } = getUserData(state);
    const payload = {
      userId,
      name: rawAddress.addressName?.trim(),
      attention: rawAddress.attention?.trim(),
      addressLine1: rawAddress.address?.trim(),
      addressLine2: rawAddress.address2?.trim(),
      addressLine3: rawAddress.address3?.trim(),
      city: rawAddress.city?.trim(),
      state: rawAddress.state?.trim(),
      zipCode: rawAddress.zipCode?.trim(),
      country: rawAddress.country?.trim(),
      phoneNumber: rawAddress.phoneNumber,
      extension: rawAddress.extension,
      districtName: rawAddress.districtName?.trim(),
      shopSuiteFloor: rawAddress.shopSuiteFloor?.trim()
    };

    return http
      .post(join(api2, '/addresses'), payload)
      .then(({ data }) => {
        if (displayNotification) {
          dispatch(showToast({ type: 'success', message: new TranslatedCopy({ id: 'global.saved' }).toString() }));
        }

        dispatch(action.success(data));

        return dispatch(requestAddressById({ addressId: data.id, isLocked: false }));
      })
      .catch(error => {
        if (displayNotification) {
          dispatch(
            showToast({
              type: 'error',
              message: new TranslatedCopy({ id: 'global.toast.systemError' }).toString(),
              error
            })
          );
        }

        dispatch(action.error(error));
      });
  }
});

// formerly updateAddress
export const saveAddress = asyncAction({
  baseName: 'SAVE_ADDRESSES',
  action: action => values => dispatch => {
    dispatch(action.start(values));

    const displayNotification = values.displayNotification !== false;
    const rawAddress = values.address;
    const addressId = rawAddress.id;
    const payload = {
      name: rawAddress.addressName?.trim(),
      attention: rawAddress.attention?.trim(),
      addressLine1: rawAddress.address?.trim(),
      addressLine2: rawAddress.address2?.trim(),
      addressLine3: rawAddress.address3?.trim(),
      city: rawAddress.city?.trim(),
      state: rawAddress.state?.trim(),
      zipCode: rawAddress.zipCode?.trim(),
      country: rawAddress.country?.trim(),
      phoneNumber: rawAddress.phoneNumber,
      extension: rawAddress.extension,
      districtName: rawAddress.districtName?.trim(),
      shopSuiteFloor: rawAddress.shopSuiteFloor?.trim()
    };

    return http
      .patch(join(api2, `/addresses/${addressId}`), payload)
      .then(({ data }) => {
        if (displayNotification) {
          dispatch(showToast({ type: 'success', message: new TranslatedCopy({ id: 'global.updated' }).toString() }));
        }

        dispatch(action.success(data));

        return dispatch(requestAddressById({ addressId, isLocked: false }));
      })
      .catch(error => {
        if (displayNotification) {
          dispatch(
            showToast({
              type: 'error',
              message: new TranslatedCopy({ id: 'global.toast.systemError' }).toString(),
              error
            })
          );
        }

        return dispatch(action.error(error));
      });
  }
});

// saveOrUpdateAddress
export const saveOrCreateAddress = () => (dispatch, getState) => {
  const state = getState();
  const { email: username } = getUserData(state);
  const fieldSelector = formValueSelector('addressBookForm');
  const address = fieldSelector(
    state,
    'id',
    'addressName',
    'attention',
    'address',
    'address2',
    'city',
    'state',
    'country',
    'zipCode',
    'phoneNumber',
    'extension',
    'districtName',
    'shopSuiteFloor'
  );

  const payload = {
    username,
    address: {
      ...address,
      country: address.country.value,
      state: address.state.value,
      city: typeof address.city === 'string' ? address.city : address.city.value
    },
    displayNotification: false
  };
  const action = payload.address.id ? saveAddress : createAddress;

  return dispatch(action(payload)).then(address => {
    dispatch(assignCartProductGroups());

    const account = getGamedayAccount(state);
    const { accountNumber } = account;

    return {
      ...DEFAULT_ADDRESS_STRUCTURE,
      shipToId: accountNumber,
      ...address.payload,
      isLocked: false,
      isActive: true,
      isCustom: true,
      type: ADDRESS_TYPES.GAMEDAY
    };
  });
};

export const deleteAddress = asyncAction({
  baseName: 'DELETE_ADDRESS',
  action: action => values => dispatch => {
    dispatch(action.start(values));

    const { addressId } = values;
    const displayNotification = values.displayNotification !== false;

    return http
      .delete(join(api2, `/addresses/${addressId}`))
      .then(({ data }) => {
        if (displayNotification) {
          dispatch(showToast({ type: 'success', message: new TranslatedCopy({ id: 'global.deleted' }).toString() }));
        }

        return dispatch(action.success(data));
      })
      .catch(error => {
        if (displayNotification) {
          dispatch(
            showToast({
              type: 'error',
              message: new TranslatedCopy({ id: 'global.toast.systemError' }).toString(),
              error
            })
          );
        }

        return dispatch(action.error(error));
      });
  }
});

export const deleteAddressAndUpdateState = address => (dispatch, getState) => {
  const { id: addressId } = address;
  const state = getState();
  const { id: username } = getUserData(state);
  const payload = { username, addressId, displayNotification: false };

  return dispatch(deleteAddress(payload)).then(response => {
    dispatch(removeAddressById(addressId));
    dispatch(assignCartProductGroups());

    return response;
  });
};

export const findAddressesForAutocomplete = asyncAction({
  baseName: 'FIND_ADDRESSES',
  action: action => ({ address, country, language }, container) => dispatch => {
    dispatch(action.start());

    return http
      .get(
        join(
          loqateFind,
          `&Text=${encodeURIComponent(
            address
          )}&Countries=${country}&Limit=5&Language=${language}&Container=${container}`
        )
      )
      .then(({ data: response }) => dispatch(action.success(response)))
      .catch(error => dispatch(action.error(error)));
  }
});

export const retrieveAddressForAutocomplete = asyncAction({
  baseName: 'RETRIEVE_ADDRESS',
  action: action => id => dispatch => {
    dispatch(action.start());

    return http
      .get(join(loqateRetrieve, `&Id=${id}&cache=true`))
      .then(({ data: response }) => dispatch(action.success(response)))
      .catch(error => dispatch(action.error(error)));
  }
});

export const googleValidateAddress = address =>
  fetch('https://addressvalidation.googleapis.com/v1:validateAddress?key=AIzaSyDC_5YTsiEQFhvyNWo0pJQxtwIrqo4TWA0', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ address })
  }).then(response => response.json());
